/* DnG motion controller: one preparation contract, grouped entrances, native scrolling. */
(() => {
  'use strict';
  if (window.__dngMotion) return;
  window.__dngMotion = true;
  const selector = window.__dngMotionSelector;
  const groups = window.__dngMotionGroups;
  if (!selector || !groups) { window.__dngMotionRelease?.(); return; }
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 990px)');
  const mobile = matchMedia('(max-width: 749px)');
  const completed = new WeakSet();
  const waiting = new Set();
  const deferredCarousel = new Set();
  const featuredCarousel = '#MainContent > [id$="__product_list_tia4Fc"]';
  const observed = new Set();
  const running = new Set();
  const depths = new Map();
  let gridCache = new WeakMap();
  let observer;
  let mutations;
  let observerRoot;
  const ease = 'cubic-bezier(.22,1,.36,1)';
  const show = el => el.setAttribute('data-dng-motion-visible', '');
  const enabled = () => !reduced.matches && !window.__dngMotionFailOpen && document.documentElement.hasAttribute('data-dng-motion-ready');
  const scrollRoot = () => desktop.matches ? document.querySelector('.page-wrapper') : null;
  function animate(el, frames, options = {}) {
    if (!enabled() || !el.animate || el.getAnimations().length) return null;
    if(mobile.matches){
      frames=frames.map((f,i)=>({opacity:f.opacity===0?.4:1,transform:i===0?'translate3d(0,10px,0)':'translate3d(0,0,0)'}));
    }
    try {
      const animation = el.animate(frames, { duration: 850, easing: ease, fill: 'backwards', ...options });
      running.add(animation);
      animation.finished.catch(() => {}).finally(() => running.delete(animation));
      return animation;
    } catch (_) { return null; }
  }
  function depth(image) {
    if (!enabled() || !desktop.matches || !('ViewTimeline' in window) || depths.size >= 3 || depths.has(image) || !image.isConnected || navigator.connection?.saveData || (navigator.deviceMemory && navigator.deviceMemory < 4)) return;
    let container = image.parentElement;
    for (let n = 0; container && n < 4; n++, container = container.parentElement) {
      const style = getComputedStyle(container);
      if (!['hidden', 'clip'].includes(style.overflowX) || !['hidden', 'clip'].includes(style.overflowY)) continue;
      try {
        const animation = image.animate([
          { scale: '1', translate: '0 0' },
          { scale: '1.05', translate: '0 -6px', offset: .5 },
          { scale: '1', translate: '0 0' }
        ], { timeline: new ViewTimeline({ subject: container, axis: 'block' }), rangeStart: 'entry 0%', rangeEnd: 'exit 100%', fill: 'none', easing: 'linear' });
        depths.set(image, animation);
      } catch (_) { /* Unsupported browsers retain the same visible image. */ }
      return;
    }
  }
  function delay(el) {
    const featured = el.closest(featuredCarousel);
    if (featured && el.matches('.product-card')) {
      const cards = Array.from(featured.querySelectorAll('.product-card'));
      const index = Math.max(0, cards.indexOf(el));
      return Math.min(400, (index % (mobile.matches ? 2 : 5)) * 100);
    }
    const card = '.product-card, [class^="ai-shop-"][class$="__card"]';
    if (el.matches(card)) {
      const grid = el.parentElement;
      let info = gridCache.get(grid);
      if (!info) {
        const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
        info = { cards: Array.from(grid.querySelectorAll(card)), columns: columns > 1 ? columns : mobile.matches ? 2 : 4 };
        gridCache.set(grid, info);
      }
      return Math.min(300, Math.max(0, info.cards.indexOf(el) % info.columns) * 100);
    }
    if (el.matches('.dngh__brand')) return 60;
    if (el.matches('.dngh__actions')) return 120;
    return 0;
  }
  function imageReady(image) {
    return new Promise(resolve => {
      let finished = false;
      const finish = ready => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        image.removeEventListener('load', loaded);
        image.removeEventListener('error', failed);
        resolve(ready);
      };
      const decode = () => {
        if (!image.naturalWidth) return finish(false);
        if (image.decode) image.decode().then(() => finish(true), () => finish(false));
        else finish(true);
      };
      const loaded = () => decode();
      const failed = () => finish(false);
      const timer = setTimeout(() => finish(false), mobile.matches ? 420 : 1000);
      image.addEventListener('load', loaded, { once: true });
      image.addEventListener('error', failed, { once: true });
      if (image.complete) decode();
    });
  }
  async function reveal(el) {
    const featured = el.closest(featuredCarousel);
    if (featured && document.hidden && enabled() && el.isConnected) {
      // A newly opened preview tab must not spend its one-shot reveal while hidden.
      deferredCarousel.add(el);
      return;
    }
    if (!enabled() || !el.isConnected || document.hidden) { show(el); return; }
    // Manufacturing section override: reuse the same initialization, observer and fail-open safeguards.
    if (el.closest('#MainContent > [id$="__section_JaFCpR"]')) {
      const video = el.matches('deferred-media');
      const link = el.matches('a');
      show(el);
      animate(el, [
        { opacity: 0, translate: `${mobile.matches ? 100 : 120}px 0` },
        { opacity: 1, translate: '0 0' }
      ], { duration: 1050, delay: video ? 0 : link ? 250 : 125 });
      return;
    }
    const media = el.matches('.hero__media-wrapper, .slide__image-container, media-gallery, deferred-media');
    const card = el.matches('.product-card, [class^="ai-shop-"][class$="__card"]');
    const image = (media || card) ? el.querySelector('img') : null;
    if (image) {
      waiting.add(el);
      const ready = await imageReady(image);
      waiting.delete(el);
      if (featured && document.hidden && enabled() && el.isConnected) { deferredCarousel.add(el); return; }
      // A slow carousel photo must not cancel the card's entire entrance.
      if ((!ready && !featured) || !enabled() || !el.isConnected || el.hasAttribute('data-dng-motion-visible')) { show(el); return; }
    }
    const fadeOnly = el.matches('.dngh__primary, .dngh__brand, .dngh__actions, media-gallery, deferred-media, [class^="orbit-"][class$="__stage"], [class^="ai-orbit-"][class$="__track"]');
    const frames = media && !mobile.matches && !el.matches('deferred-media')
      ? [{ opacity: 0, clipPath: 'inset(100% 0 0 0)' }, { opacity: 1, clipPath: 'inset(0)' }]
      : [{ opacity: 0, ...(fadeOnly ? {} : { translate: `0 ${mobile.matches ? 35 : 45}px` }) }, { opacity: 1, ...(fadeOnly ? {} : { translate: '0 0' }) }];
    show(el);
    const animation = animate(el, frames, { duration: mobile.matches ? 390 : media ? 950 : 850, delay: mobile.matches ? 0 : delay(el) });
    if (image && el.matches('.hero__media-wrapper, .slide__image-container')) {
      const scale = mobile.matches ? null : animate(image, [{ scale: '1.05' }, { scale: '1' }], { duration: 950 });
      (scale || animation)?.finished.then(() => depth(image)).catch(() => {});
    }
  }
  function scan(root = document) {
    if (!enabled() || !observer) return;
    const elements = Array.from(root.querySelectorAll(selector));
    if (root instanceof Element && root.matches(selector)) elements.unshift(root);
    // Select group owners before their children, preserving existing nested component motion.
    elements.forEach(el => {
      if (completed.has(el) || observed.has(el) || waiting.has(el)) return;
      if (el.parentElement?.closest(groups) || el.closest('dialog, [role="dialog"], .visually-hidden, [aria-live], [role="status"]')) {
        show(el); completed.add(el); return;
      }
      // Hidden tabs/slides are not finished: attribute observation prepares them on activation.
      if (el.closest('[hidden], [aria-hidden="true"], [inert]') || !el.getClientRects().length) return;
      if (el.hasAttribute('data-dng-motion-visible')) { completed.add(el); return; }
      const rect = el.getBoundingClientRect();
      const bounds = observerRoot?.getBoundingClientRect() || { top: 0, bottom: innerHeight, left: 0, right: innerWidth };
      if (rect.bottom <= bounds.top) { show(el); completed.add(el); return; }
      if (rect.top < bounds.bottom && rect.bottom > bounds.top && rect.right > bounds.left && rect.left < bounds.right) {
        completed.add(el); reveal(el).catch(() => show(el));
      } else { observed.add(el); observer.observe(el); }
    });
  }
  function createObserver() {
    observer?.disconnect();
    observed.clear();
    observerRoot = scrollRoot();
    observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target); observed.delete(entry.target);
        if (completed.has(entry.target)) return;
        completed.add(entry.target);
        reveal(entry.target).catch(() => show(entry.target));
      });
    }, { root: observerRoot, rootMargin: '0px 0px -8% 0px', threshold: 0 });
  }
  function prune() {
    deferredCarousel.forEach(el => { if (!el.isConnected) deferredCarousel.delete(el); });
    observed.forEach(el => { if (!el.isConnected) { observer?.unobserve(el); observed.delete(el); } });
    depths.forEach((animation, image) => { if (!image.isConnected) { animation.cancel(); depths.delete(image); } });
  }
  function init() {
    if (!enabled()) return;
    if (!observer) createObserver();
    scan();
    clearTimeout(window.__dngMotionStartupGuard);
    if (!mutations && document.body) {
      mutations = new MutationObserver(records => {
        try {
          const roots = new Set();
          let removed = false;
          records.forEach(record => {
            if (record.type === 'attributes') roots.add(record.target);
            else {
              removed ||= record.removedNodes.length > 0;
              record.addedNodes.forEach(node => { if (node instanceof Element && !node.closest('[data-dng-mosaic]')) roots.add(node); });
            }
          });
          if (removed) { prune(); gridCache = new WeakMap(); }
          roots.forEach(root => scan(root));
        } catch (_) { release(); }
      });
      mutations.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'inert'] });
    }
  }
  function interact(event) {
    let el = event.target instanceof Element ? event.target : null;
    while (el) {
      if (el.matches(selector)) { show(el); completed.add(el); observer?.unobserve(el); observed.delete(el); }
      el = el.parentElement;
    }
    running.forEach(animation => {
      const target = animation.effect?.target;
      if (target === event.target || target?.contains(event.target)) animation.cancel();
    });
  }
  function pause() {
    running.forEach(a => a.cancel());
    waiting.forEach(show);
    depths.forEach(a => a.cancel()); depths.clear();
  }
  function release() {
    pause();
    observer?.disconnect(); observer = null;
    observed.forEach(show); observed.clear();
    deferredCarousel.forEach(show); deferredCarousel.clear();
    mutations?.disconnect(); mutations = null;
    window.__dngMotionRelease?.();
  }
  window.DnGMotion = { scan, pause, release };
  document.addEventListener('pointerdown', interact, { passive: true });
  document.addEventListener('focusin', interact);
  document.addEventListener('shopify:section:load', event => { prune(); scan(event.target); });
  document.addEventListener('shopify:section:unload', prune);
  reduced.addEventListener('change', release);
  desktop.addEventListener('change', () => { gridCache = new WeakMap(); pause(); if (enabled()) { createObserver(); scan(); } });
  mobile.addEventListener('change', () => { gridCache = new WeakMap(); });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden || !enabled() || !deferredCarousel.size) return;
    deferredCarousel.forEach(el => { completed.delete(el); });
    deferredCarousel.clear();
    const section = document.querySelector(featuredCarousel);
    if (section) scan(section);
  });
  window.addEventListener('pagehide', release);
  window.addEventListener('pageshow', event => { if (event.persisted) release(); });
  // Defer waits only for document parsing, never window.load or all images/videos.
  try { init(); } catch (_) { release(); }
})();
